"use client";

import {
  grantAnalyticsConsent,
  revokeAnalyticsConsent,
} from "@pex/analytics/client";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Switch,
} from "@pex/ui-web";
import { useRouter } from "next/navigation";
import { useEffect, useState, useTransition } from "react";
import { saveConsent } from "./actions";
import { CONSENT_COOKIE_NAME, CONSENT_COOKIE_VERSION } from "./constants";
import type { ConsentCategories, ConsentState } from "./types";

/**
 * Read the consent cookie client-side (document.cookie).
 * Returns null if not set or the version is outdated.
 * Use server-side `getConsent()` in Server Components instead.
 */
export function getConsentFromCookie(): ConsentState | null {
  if (typeof document === "undefined") {
    return null;
  }

  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${CONSENT_COOKIE_NAME}=`));

  if (!match) {
    return null;
  }

  try {
    const raw = decodeURIComponent(match.split("=").slice(1).join("="));
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== CONSENT_COOKIE_VERSION) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// useIsDesktop — deferred after mount to avoid hydration mismatch
// ---------------------------------------------------------------------------

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    setIsDesktop(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return isDesktop;
}

// ---------------------------------------------------------------------------
// Shared preferences content — rendered inside Dialog or Sheet
// ---------------------------------------------------------------------------

interface PreferencesContentProps {
  analytics: boolean;
  isDialog?: boolean;
  isPending: boolean;
  marketing: boolean;
  onAnalyticsChange: (value: boolean) => void;
  onMarketingChange: (value: boolean) => void;
  onRejectAll: () => void;
  onSave: () => void;
}

function PreferencesContent({
  analytics,
  marketing,
  onAnalyticsChange,
  onMarketingChange,
  onRejectAll,
  onSave,
  isPending,
  isDialog = false,
}: PreferencesContentProps) {
  const Title = isDialog ? DialogTitle : SheetTitle;
  const Description = isDialog ? DialogDescription : SheetDescription;
  const Header = isDialog ? DialogHeader : SheetHeader;
  const Footer = isDialog ? DialogFooter : SheetFooter;

  return (
    <>
      <Header>
        <Title>Cookie preferences</Title>
        <Description>
          Manage which cookies you allow. Essential cookies are required for the
          site to function and cannot be disabled.
        </Description>
      </Header>

      <div className="space-y-5 px-6 py-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-medium text-sm">Essential</p>
            <p className="text-muted-foreground text-xs">
              Authentication, security, and basic functionality.
            </p>
          </div>
          <Switch aria-label="Essential cookies (always on)" checked disabled />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-medium text-sm">Analytics</p>
            <p className="text-muted-foreground text-xs">
              Helps us understand how visitors use the site so we can improve
              it.
            </p>
          </div>
          <Switch
            aria-label="Analytics cookies"
            checked={analytics}
            onCheckedChange={onAnalyticsChange}
          />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-medium text-sm">Marketing</p>
            <p className="text-muted-foreground text-xs">
              Allows us to show you relevant content and measure campaign
              effectiveness.
            </p>
          </div>
          <Switch
            aria-label="Marketing cookies"
            checked={marketing}
            onCheckedChange={onMarketingChange}
          />
        </div>
      </div>

      <Footer>
        <Button
          disabled={isPending}
          onClick={onRejectAll}
          size="sm"
          variant="outline"
        >
          Reject all
        </Button>
        <Button disabled={isPending} onClick={onSave} size="sm">
          Save preferences
        </Button>
      </Footer>
    </>
  );
}

// ---------------------------------------------------------------------------
// PreferencesSheet — dialog on desktop, bottom sheet on mobile
// ---------------------------------------------------------------------------

interface PreferencesSheetProps {
  initial?: ConsentCategories;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (categories: ConsentCategories) => void;
  open: boolean;
}

function PreferencesSheet({
  open,
  onOpenChange,
  initial,
  onSave,
  isPending,
}: PreferencesSheetProps) {
  const isDesktop = useIsDesktop();
  const [analytics, setAnalytics] = useState(initial?.analytics ?? false);
  const [marketing, setMarketing] = useState(initial?.marketing ?? false);

  useEffect(() => {
    if (open) {
      setAnalytics(initial?.analytics ?? false);
      setMarketing(initial?.marketing ?? false);
    }
  }, [open, initial?.analytics, initial?.marketing]);

  const contentProps: PreferencesContentProps = {
    analytics,
    marketing,
    onAnalyticsChange: setAnalytics,
    onMarketingChange: setMarketing,
    onRejectAll: () => onSave({ analytics: false, marketing: false }),
    onSave: () => onSave({ analytics, marketing }),
    isPending,
  };

  if (isDesktop) {
    return (
      <Dialog onOpenChange={onOpenChange} open={open}>
        <DialogContent className="sm:max-w-md">
          <PreferencesContent {...contentProps} isDialog />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetContent className="mx-auto max-w-lg rounded-t-lg" side="bottom">
        <PreferencesContent {...contentProps} />
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// CookieBanner — shown when no consent cookie exists
// ---------------------------------------------------------------------------

interface CookieBannerProps {
  onConsent?: (categories: ConsentCategories) => void;
  privacyPolicyUrl: string;
}

export function CookieBanner({
  onConsent,
  privacyPolicyUrl,
}: CookieBannerProps) {
  const [isPending, startTransition] = useTransition();
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [hiding, setHiding] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (getConsentFromCookie() === null) {
      setVisible(true);
    }
  }, []);

  if (!visible) {
    return null;
  }

  function handleSave(categories: ConsentCategories) {
    startTransition(async () => {
      await saveConsent(categories);
      onConsent?.(categories);
      setPreferencesOpen(false);
      // Wait for the preferences modal exit animation to finish before
      // sliding the banner away — avoids the two animations conflicting.
      setTimeout(() => {
        setHiding(true);
        setTimeout(() => setVisible(false), 350);
      }, 200);
    });
  }

  return (
    <>
      <div
        aria-label="Cookie consent"
        aria-modal="false"
        className={`fixed right-0 bottom-0 left-0 z-50 border-t bg-background transition-[transform,opacity] duration-300 ease-in ${hiding ? "translate-y-full opacity-0" : ""}`}
        role="dialog"
      >
        <div className="mx-auto flex max-w-screen-xl flex-wrap items-center justify-between gap-4 px-6 py-4">
          <p className="max-w-prose text-muted-foreground text-sm">
            We use cookies to understand how our services are used and to
            deliver relevant content. Essential cookies are always on.{" "}
            <a
              className="underline underline-offset-4 hover:text-foreground"
              href={privacyPolicyUrl}
            >
              Privacy Policy
            </a>
          </p>

          <div className="flex shrink-0 items-center gap-2">
            <Button
              disabled={isPending}
              onClick={() => setPreferencesOpen(true)}
              size="sm"
              variant="outline"
            >
              Preferences
            </Button>
            <Button
              disabled={isPending}
              onClick={() => handleSave({ analytics: false, marketing: false })}
              size="sm"
              variant="outline"
            >
              Reject all
            </Button>
            <Button
              disabled={isPending}
              onClick={() => handleSave({ analytics: true, marketing: true })}
              size="sm"
            >
              Accept all
            </Button>
          </div>
        </div>
      </div>

      <PreferencesSheet
        isPending={isPending}
        onOpenChange={setPreferencesOpen}
        onSave={handleSave}
        open={preferencesOpen}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// CookiePreferencesButton — for footers / settings pages
// ---------------------------------------------------------------------------

interface CookiePreferencesButtonProps {
  className?: string;
}

export function CookiePreferencesButton({
  className,
}: CookiePreferencesButtonProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<ConsentCategories | undefined>(
    undefined
  );

  function handleOpenChange(next: boolean) {
    if (next) {
      const stored = getConsentFromCookie();
      setCurrent(stored?.categories);
    }
    setOpen(next);
  }

  function handleSave(categories: ConsentCategories) {
    startTransition(async () => {
      await saveConsent(categories);
      setOpen(false);
      router.refresh();
    });
  }

  return (
    <>
      <Button
        className={className}
        onClick={() => handleOpenChange(true)}
        size="sm"
        variant="ghost"
      >
        Cookie Preferences
      </Button>

      <PreferencesSheet
        initial={current}
        isPending={isPending}
        onOpenChange={handleOpenChange}
        onSave={handleSave}
        open={open}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// ConsentBanner — app-ready wrapper: applies analytics consent + builds the
// privacy policy URL. Renders the shared CookieBanner. Each app's layout can
// render this directly instead of maintaining its own per-app wrapper.
// ---------------------------------------------------------------------------

function applyAnalyticsConsent(categories: ConsentCategories) {
  if (categories.analytics) {
    grantAnalyticsConsent();
  } else {
    revokeAnalyticsConsent();
  }
}

interface ConsentBannerProps {
  /**
   * Override the privacy policy link. Defaults to
   * `${NEXT_PUBLIC_WEBSITE_URL}/legal/privacy-policy`, which is identical
   * across every app today.
   */
  privacyPolicyUrl?: string;
}

export function ConsentBanner({ privacyPolicyUrl }: ConsentBannerProps = {}) {
  useEffect(() => {
    const stored = getConsentFromCookie();
    if (stored) {
      applyAnalyticsConsent(stored.categories);
    }
  }, []);

  const url =
    privacyPolicyUrl ??
    `${process.env.NEXT_PUBLIC_WEBSITE_URL}/legal/privacy-policy`;

  return (
    <CookieBanner onConsent={applyAnalyticsConsent} privacyPolicyUrl={url} />
  );
}
